import React, { useState, useEffect, useMemo, useContext } from "react";
import {
  Grid,
  GridColumn,
  Segment,
  GridRow,
  Label,
  Table,
  Header,
  Button,
  Loader,
  Message,
  Icon,
  TableBody,
  TableRow,
  TableCell,
} from "semantic-ui-react";
import BatteryImage from "../SharingPoints/BatteryImage";
import { SimpleMap } from "../helpers/maps/SimpleMarkerMap";
import { Link, navigate } from "@reach/router";
import { useFormatTimestamp } from "../../hooks/use-format-timestamp";
import { isArray, upperFirst } from "lodash";
import { useSelector } from "react-redux";
import {
  BatteryIdDropdown,
  ExportDataAsCsv,
  SWBLabel,
  SWBNavigateTo,
} from "../UtilComponents";
import "./style.scss";
import moment from "moment";
import { AxiosContext } from "../../context/AxiosContext";
import SwapTasksTable from "./SwapTasksTable";
import SwapStateHistoryTable from "./SwapStateHistoryTable";
import { SwapContext } from "../../context/SwapContext";
import SwapLogging from "./SwapLogging";
import UserSwapInteractionTable from "./UserSwapInteractionTable";
import BatteryEventsTable from "./BatteryEventsTable";

export default ({ swapId }) => {
  const [colorMapping, setColorMapping] = useState();
  const { user_group } = useSelector((state) => state.session);
  const { axiosInstance } = useContext(AxiosContext);
  const { fetchSwapById, swap, abortSwap, abortSwapLoading, setSwap } =
    useContext(SwapContext);

  // const generateHexColor = () => {
  //   let temp = `#${Math.floor(Math.random() * 16777215).toString(16)}`;
  //   if (!colorMapping) return temp;
  //   while (!Object.values(colorMapping).includes(temp)) {
  //     temp = `#${Math.floor(Math.random() * 16777215).toString(16)}`;
  //   }
  //   return temp;
  // };

  // useEffect(() => {
  //   if (!swap) return;
  //   const temp = {};
  //   swap.events.forEach(({ battery_id }) => {
  //     temp[battery_id] = {
  //       color: generateHexColor(),
  //       count: temp[battery_id] ? temp[battery_id]?.count + 1 : 1,
  //     };
  //     setColorMapping(temp);
  //   });
  //   return () => {
  //     setColorMapping(null);
  //   };
  // }, [swap?.events]);

  useEffect(() => {
    if (!axiosInstance) return;
    fetchSwapById(swapId);
    return () => setSwap(null);
  }, [axiosInstance]);

  const convertTimestamp = (timestamp) => {
    let d = new Date(+timestamp);
    return (
      `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}` +
      " - " +
      d.getHours() +
      ":" +
      d.getMinutes() +
      ":" +
      d.getSeconds()
    );
  };

  const filename = `swap_export_${swapId}`;

  const data = useMemo(() => {
    if (!swap) return;
    if (!swap.events) return;
    return swap.events.map(
      ({
        action,
        battery_id,
        timestamp,
        module_number,
        slot_number,
        soc_value,
      }) => {
        return {
          module_number,
          slot_number,
          timestamp: convertTimestamp(timestamp),
          battery_id,
          state_of_charge: soc_value ? soc_value + "%" : "-",
          action,
        };
      }
    );
  }, [swap]);

  return (
    <Grid>
      {!swap && (
        <GridColumn width="16" style={{ marginLeft: 20 }}>
          <Loader active inline size="large" />
        </GridColumn>
      )}
      {swap && (
        <>
          <GridRow columns="1">
            <GridColumn style={{ color: "black", height: "400px" }}>
              <Segment style={{ padding: 0, height: "400px" }}>
                <SimpleMap latLng={[+swap.gps_latitude, +swap.gps_longitude]} />
              </Segment>
            </GridColumn>
          </GridRow>

          <GridRow>
            <Grid
              stackable
              className="grid-swap-segment"
              style={{ margin: "auto" }}
              columns="1"
            >
              <GridColumn style={{ color: "black" }}>
                <Segment>
                  <Grid>
                    <GridColumn
                      width="16"
                      style={{ margin: "auto" }}
                      textAlign="center"
                    >
                      <h3 style={{ color: "black", fontSize: 30 }}>
                        📍{" "}
                        {`${swap.street} ${swap.street_number}, ${swap.zipcode} ${swap.city}`}
                      </h3>
                    </GridColumn>
                    <GridColumn
                      width="16"
                      textAlign="center"
                      style={{ marginTop: "14px", marginBottom: "14px" }}
                    >
                      <BatteryImage
                        type={swap.battery_type}
                        view={"swapView"}
                        width="auto"
                        height="150px"
                        float="middle"
                        margin="auto"
                      />
                    </GridColumn>
                  </Grid>
                </Segment>
                <Segment>
                  <span
                    style={{
                      fontWeight: "bold",
                      fontSize: 22,
                      color: "#444",
                      marginBottom: 20,
                    }}
                  >
                    👤 User Information
                  </span>
                  <div className="swap-detail-key-value-grid-flex">
                    <span className="swap-detail-view-key">Requested by</span>{" "}
                    <span className="swap-detail-view-value">
                      <SWBNavigateTo
                        style={{ fontWeight: 16 }}
                        view="UserProfileView"
                        propId={swap.created_by}
                      />
                    </span>
                  </div>
                  <div className="swap-detail-key-value-grid-flex">
                    <span className="swap-detail-view-key">User group</span>
                    <span className="swap-detail-view-value">
                      {swap.swap_status_map
                        ? swap.user_group_name.substring(0, 35)
                        : "-"}
                    </span>
                  </div>
                  <div className="swap-detail-key-value-grid-flex">
                    <span className="swap-detail-view-key">App version</span>
                    <span className="swap-detail-view-value">
                      {`v${swap?.user_device_info?.app_version} (${swap?.user_device_info?.os_name})` ||
                        "-"}
                    </span>
                  </div>
                  <div className="swap-detail-key-value-grid-flex">
                    <span className="swap-detail-view-key">Device</span>
                    <span className="swap-detail-view-value">
                      {`${swap?.user_device_info?.model_name}` || "-"}
                    </span>
                  </div>
                </Segment>

                <Segment>
                  <span
                    style={{
                      fontWeight: "bold",
                      fontSize: 22,
                      color: "#444",
                      marginBottom: 20,
                    }}
                  >
                    🔋 Battery Information
                  </span>
                  <div className="swap-detail-key-value-grid-flex">
                    <span className="swap-detail-view-key">Sharingpoint</span>{" "}
                    <span className="swap-detail-view-value">
                      <Link to={`/sharingpoints/${swap.sp_serial_number}`}>
                        {swap.sp_serial_number}
                      </Link>
                    </span>
                  </div>

                  <div className="swap-detail-key-value-grid-flex">
                    <span className="swap-detail-view-key">Battery type</span>{" "}
                    <span className="swap-detail-view-value">
                      {swap.battery_type}
                    </span>
                  </div>

                  <div className="swap-detail-key-value-grid-flex">
                    <span className="swap-detail-view-key">Requested</span>{" "}
                    <span className="swap-detail-view-value">
                      {swap.requested !== -1 ? (
                        swap.requested
                      ) : (
                        <Label
                          content={"FILL-IN"}
                          color="blue"
                          circular
                          style={{ width: 100 }}
                        />
                      )}
                    </span>
                  </div>

                  <div className="swap-detail-key-value-grid-flex">
                    <span className="swap-detail-view-key">Out/In</span>{" "}
                    <span className="swap-detail-view-value">
                      {swap.count_out}/{swap.count_in}
                    </span>
                  </div>
                </Segment>

                <Segment style={{ display: "flex", flexDirection: "column" }}>
                  <div style={{ display: "flex", flexDirection: "row" }}>
                    <span
                      style={{
                        fontWeight: "bold",
                        fontSize: 22,
                        color: "#444",
                        marginBottom: 20,
                      }}
                    >
                      {iconMapping[swap.state]} Swap Status ({swap.state})
                    </span>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      fontSize: 16,
                      color: "#444",
                      gap: 14,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        fontSize: 16,
                      }}
                    >
                      <span>Requested at</span>
                      <span>
                        {moment(swap.created_at).format("DD-MM-YYYY HH:mm:ss")}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        fontSize: 16,
                      }}
                    >
                      <span>Updated at</span>
                      <span>
                        {moment(swap.updated_at).format("DD-MM-YYYY HH:mm:ss")}
                      </span>
                    </div>
                  </div>
                </Segment>
              </GridColumn>
              {swap.swap_state_history ? (
                <SwapStateHistoryTable
                  swapStateHistory={swap.swap_state_history}
                />
              ) : null}
              {swap.tasks ? <SwapTasksTable swapTasks={swap.tasks} /> : null}
              {swap.user_interactions ? (
                <UserSwapInteractionTable
                  user_interactions={swap.user_interactions}
                />
              ) : null}

              <GridColumn>
                <Segment>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <Header style={{ color: "#444" }}>
                      {swap.door_events.length} Door events
                    </Header>
                  </div>
                  <Table>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell className="left-header">
                          Event ID
                        </Table.HeaderCell>
                        <Table.HeaderCell className="inner-header">
                          Module
                        </Table.HeaderCell>
                        {swap.swap_type === "SINGLE_LOCK_SWAP" && (
                          <Table.HeaderCell className="inner-header">
                            Slot
                          </Table.HeaderCell>
                        )}

                        <Table.HeaderCell className="inner-header">
                          Timestamp
                        </Table.HeaderCell>

                        <Table.HeaderCell className="right-header">
                          State
                        </Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <TableBody>
                      {swap.door_events.map(
                        ({
                          module_number,
                          door_state,
                          created_at,
                          event_id,
                          slot_number,
                        }) => (
                          <TableRow>
                            <TableCell>{event_id}</TableCell>
                            <TableCell>{module_number}</TableCell>
                            {swap.swap_type === "SINGLE_LOCK_SWAP" && (
                              <TableCell>{slot_number}</TableCell>
                            )}
                            <TableCell>
                              {moment(created_at).format("DD-MM-YYYY HH:mm:ss")}
                            </TableCell>
                            <TableCell>
                              <SWBLabel
                                content={door_state}
                                activated={door_state === "open"}
                              />
                            </TableCell>
                          </TableRow>
                        )
                      )}
                    </TableBody>
                  </Table>
                  {!swap.door_events.length && (
                    <span
                      style={{
                        alignSelf: "center",
                        color: "#444",
                        width: "100%",
                      }}
                    >
                      no data available
                    </span>
                  )}
                </Segment>
              </GridColumn>
              <BatteryEventsTable {...{ swap, data, filename, user_group }} />
              {swap?.is_dev_user &&
              swap.swap_logging &&
              swap.swap_logging.length > 0 ? (
                <GridColumn>
                  <SwapLogging logs={swap.swap_logging} />
                </GridColumn>
              ) : null}

              {!["ABORTED", "COMPLETED"].includes(swap.state) ? (
                <GridColumn>
                  <Button
                    fluid
                    onClick={() => abortSwap(swapId)}
                    negative
                    loading={abortSwapLoading}
                  >
                    Abort
                  </Button>
                </GridColumn>
              ) : null}
            </Grid>
          </GridRow>
        </>
      )}
    </Grid>
  );
};

const iconMapping = {
  OPEN: "🟢",
  COMPLETED: "✅",
  ERROR: "❌",
  ABORTED: "⏹️",
};
